import React from "react";
import { IPersonaStyles, Persona, PersonaSize } from "@fluentui/react";
import { ColorPair, generateColorForValue } from "../../../utilities/DisplayValueFormatUtil";
import { splitStringByNewline } from "../../../src/taskpane/components/helpers/stringHelper";

/**
 * Shared per-type field cell renderers for the content-manager grids.
 *
 * This is the single implementation behind BOTH the single-library documents grid
 * (useContentManagerBody's column chain) and the cross-library "All documents" grid
 * (aggregateColumnRenderers). The logic — including tenant CustomFormatter pill
 * rendering and NamePhoto persona cells — was extracted verbatim from
 * useContentManagerBody so the two grids cannot drift apart on field rendering.
 *
 * Everything here is pure presentation: closures over grid/navigation state stay in
 * the callers (file links, folder clicks, thumbnails, URL commands).
 */

// ----- styles (moved from useContentManagerBody; re-exported there for existing importers) -----

export const gridColumnMultiValueContainer: React.CSSProperties = {
    display: "inline-flex",
    flexDirection: "column",
    maxWidth: "100%"
};

export const gridRowMultiValueStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center"
};

export const pillContainer: React.CSSProperties = {
    display: "inline-flex",
    flexWrap: "wrap",
    gap: 5,
    maxWidth: "100%"
};

export const pillStyle: React.CSSProperties = {
    boxSizing: "border-box",
    padding: "4px 8px 5px 8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    height: 24,
    borderRadius: 16,
    display: "inline-flex",
    alignItems: "center"
};

export const peoplePill: React.CSSProperties = {
    backgroundColor: "var(--db-border, #edebe9)",
    height: 24,
    borderRadius: 12,
    display: "inline-flex",
    alignItems: "center"
};

export const peoplePillPersonaStyles: Partial<IPersonaStyles> = {
    details: {
        paddingLeft: 5
    },
    primaryText: {
        fontSize: 12,
        lineHeight: 18
    }
};

export const titleGridCellStyle: React.CSSProperties = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
};

export const textGridCellStyle: React.CSSProperties = {
    whiteSpace: "normal"
};

export const noteGridCellStyle: React.CSSProperties = {
    whiteSpace: "break-spaces"
};

// ----- options -----

export interface ISharedCellRenderOptions {
    // The list field's CustomFormatter (truthy → tenant colored-pill rendering).
    customFormatter?: any;
    // Tenant metadata color configuration (state.tenantInfo.metadataTextColors).
    metadataTextColors?: any;
    // Third argument to generateColorForValue for single-text cells (the existing
    // single-library grid passes !datasourceBoundField here).
    colorFallback?: boolean;
    // Style override for plain text cells (e.g. titleGridCellStyle for Title).
    cellStyle?: React.CSSProperties;
    // The list field's UserDisplayOptions ("NamePhoto" → persona pills).
    userDisplayOptions?: string;
}

const renderPills = (values: any[], getLabel: (entry: any) => string, options: ISharedCellRenderOptions): JSX.Element => (
    <div style={pillContainer}>
        {values.map((entry, index) => {
            const label = getLabel(entry);
            const valueColor: ColorPair = generateColorForValue(label, options.metadataTextColors);
            return <div key={index} style={{ ...pillStyle, backgroundColor: valueColor.backgroundColor }}>
                <span style={{ color: valueColor.textColor, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
            </div>;
        })}
    </div>
);

const renderPlainMultiValue = (values: any[], getLabel: (entry: any) => string): JSX.Element => (
    <div style={gridColumnMultiValueContainer}>
        {values.map((entry, index) => (
            <div key={index} style={gridRowMultiValueStyle}>
                <span>{getLabel(entry)}</span>
            </div>
        ))}
    </div>
);

// ----- per-type renderers -----

export const renderMultiChoiceCell = (value: any, options: ISharedCellRenderOptions = {}): JSX.Element => {
    if (!Array.isArray(value)) return <></>;
    if (options.customFormatter) {
        return renderPills(value, entry => entry, options);
    }
    return renderPlainMultiValue(value, entry => entry);
};

export const renderChoiceCell = (value: any, options: ISharedCellRenderOptions = {}): JSX.Element => {
    if (value === '' || value === null || value === undefined) return <></>;
    if (options.customFormatter) {
        const valueColor: ColorPair = generateColorForValue(value, options.metadataTextColors);
        return <div style={pillContainer}>
            <div style={{ ...pillStyle, backgroundColor: valueColor.backgroundColor }}>
                <span style={{ color: valueColor.textColor, overflow: "hidden", textOverflow: "ellipsis" }}>{value}</span>
            </div>
        </div>;
    }
    return <span style={textGridCellStyle}>{value}</span>;
};

/** Multi-value text (datasource-bound multi-select fields, pre-split by the caller). */
export const renderTextMultiValueCell = (values: string[], options: ISharedCellRenderOptions = {}): JSX.Element => {
    if (!Array.isArray(values) || values.length === 0) return <></>;
    if (options.customFormatter) {
        return renderPills(values, entry => entry, options);
    }
    return renderPlainMultiValue(values, entry => entry);
};

export const renderTextCell = (value: any, options: ISharedCellRenderOptions = {}): JSX.Element => {
    if (value === '' || value === null || value === undefined) return <></>;
    if (typeof value === "object") return <></>;
    if (options.customFormatter) {
        const valueColor: ColorPair = generateColorForValue(value, options.metadataTextColors, options.colorFallback);
        return <div style={pillContainer}>
            <div style={{ ...pillStyle, backgroundColor: valueColor.backgroundColor }}>
                <span style={{ color: valueColor.textColor, overflow: "hidden", textOverflow: "ellipsis" }}>{value}</span>
            </div>
        </div>;
    }
    return <span style={options.cellStyle ?? textGridCellStyle}>{value}</span>;
};

export const renderLookupCell = (value: any, options: ISharedCellRenderOptions = {}): JSX.Element => {
    // Some base Lookup fields (e.g. ItemChildCount/FolderChildCount) come back as a
    // plain scalar rather than a lookup array; render it directly instead of blank.
    // Only render primitives — an object here would crash React's child renderer.
    if (!Array.isArray(value)) {
        return value != null && value !== '' && typeof value !== 'object'
            ? <span style={textGridCellStyle}>{value}</span>
            : <></>;
    }
    if (options.customFormatter) {
        return renderPills(value, entry => entry?.lookupValue, options);
    }
    return renderPlainMultiValue(value, entry => entry?.lookupValue);
};

export const renderTaxonomyCell = (value: any): JSX.Element => (
    <span style={textGridCellStyle}>{value?.Label}</span>
);

export const renderTaxonomyMultiCell = (value: any): JSX.Element => (
    <>
        {Array.isArray(value) && renderPlainMultiValue(value, entry => entry?.Label)}
    </>
);

export const renderDateCell = (value: any, format: string, formatDate: (value: string, format: string) => string): JSX.Element => (
    <>
        {value && <span>{formatDate(value, format)}</span>}
    </>
);

export const renderUserCell = (value: any, options: ISharedCellRenderOptions = {}): JSX.Element => {
    if (options.userDisplayOptions === "NamePhoto") {
        return <div>
            <div style={pillContainer}>
                {Array.isArray(value) && value.map(entry => (
                    <div key={entry.id} style={peoplePill}>
                        <Persona styles={peoplePillPersonaStyles} text={entry.value || entry.title} size={PersonaSize.size24} showOverflowTooltip={false} />
                    </div>
                ))}
            </div>
        </div>;
    }
    return <div style={gridColumnMultiValueContainer}>
        {Array.isArray(value) && value.map(entry => (
            <div key={entry.id} style={gridRowMultiValueStyle}>
                <span>{entry.value || entry.title}</span>
            </div>
        ))}
    </div>;
};

export const renderNoteCell = (value: any): JSX.Element => {
    const lines = splitStringByNewline(value);
    return <span style={noteGridCellStyle}>{lines.join("\r\n")}</span>;
};

export const renderBooleanCell = (rawCheckedValue: any, headerLength: number): JSX.Element => {
    const booleanCellContentStyle: React.CSSProperties = {
        display: "flex",
        fontSize: 14,
        justifyContent: "center",
        width: `${headerLength}ch`
    };
    return <span style={booleanCellContentStyle}>{rawCheckedValue === "1" && "✓"}</span>;
};
